import { buildEECProduct } from '@/utils/tracking';
import { localStorageCheck } from '@/utils/localStorage';
import { debounce } from 'lodash';
import { useLoyalty } from '@/composables/useLoyalty';
import * as Sentry from '@sentry/vue';
import { useToastStore } from '@/stores/toast';

export const gtmEECProducts = async (lineItemsDeltas, client) => {
  return await Promise.all(
    lineItemsDeltas.map(async ({ lineItem, delta }) => {
      const { variant } = lineItem;
      const shopifyProduct = await client.product.fetch(variant.product.id);

      const eecProduct = buildEECProduct(variant, shopifyProduct, delta, undefined, lineItem);

      return eecProduct;
    })
  );
};

export const useCartStore = defineStore('cart', {
  state: () => ({
    isOpen: false,
    cart: {},
    checkout: {},
    payload: {},
    errors: [],
    isAsyncSubmitting: 0,
    isSubmitting: 0,
    freeShippingThreshold: 0,
  }),

  getters: {
    isProcessing: (state) => {
      return state.isSubmitting > 0 || state.isAsyncSubmitting > 0 || Object.keys(state.payload).length > 0;
    },

    lineItems: (state) => {
      if (state.cart?.lines?.nodes?.length) {
        return state.cart.lines.nodes;
      }

      return [];
    },

    cartCount: (state) => {
      if (!state.cart) {
        return 0;
      }

      return state.cart.totalQuantity;
    },

    hasDiscountApplied: (state) => {
      if (!state.cart) return false;

      return (
        state.cart.discountCodes &&
        state.cart.discountCodes.length >= 1 &&
        state.cart.discountCodes.some((d) => d.applicable)
      );
    },

    discountValue() {
      let cartDiscount = 0;
      let productDiscounts = 0;
      let currencyCode = this.cart?.cost?.totalAmount?.currencyCode || 'EUR';

      if (this.cart.discountAllocations && this.cart.discountAllocations.length > 0) {
        cartDiscount = this.cart.discountAllocations.reduce((acc, curr) => acc + +curr.discountedAmount.amount, 0);
        currencyCode = this.cart.discountAllocations[0].discountedAmount.currencyCode;
      }

      if (this.lineItems.length > 0) {
        productDiscounts = this.lineItems.reduce((acc, curr) => {
          if (curr.discountAllocations && curr.discountAllocations.length > 0) {
            // Use the currency code from the first line item discount if no cart-level discount exists
            if (curr.discountAllocations.length > 0) {
              currencyCode = curr.discountAllocations[0].discountedAmount.currencyCode;
            }
            return acc + curr.discountAllocations.reduce((acc, curr) => acc + +curr.discountedAmount.amount, 0);
          }
          return acc;
        }, 0);
      }

      return { amount: cartDiscount + productDiscounts, currencyCode };
    },

    appliedDiscountCodes() {
      if (this.hasDiscountApplied) {
        return this.cart.discountCodes.filter((node) => node.applicable).map((node) => node.code);
      }

      return [];
    },

    hasFreeShipping: (state) => {
      try {
        const totalAmount = Number.parseFloat(state.cart.cost.totalAmount?.amount) ?? 0;

        return totalAmount >= state.freeShippingThreshold;
      } catch (e) {
        return false;
      }
    },
  },

  actions: {
    async open() {
      const { $_gtm } = useNuxtApp();
      await this.initCart();

      if (!this.isOpen) {
        this.isOpen = true;
        $_gtm.push({
          event: 'virtPageView',
          page_type: 'cartFlyout',
          page_path: '/cart/flyout',
          page_title: 'Cart Flyout',
          content_category: 'cart',
          collection: null,
          search_results: null,
          product_category: null,
          product_subcategory: null,
          content_subcategory: null,
        });
      }
    },

    close() {
      this.isOpen = false;
    },

    /**
     * initialize cart from locally stored cartId
     * or register new cart
     */
    async initCart() {
      const { $i18n, $shopifyClient } = useNuxtApp();

      if (this.cart && this.cart.id) {
        // checks for loyalty email update
        if (this.cart.buyerIdentity && !this.cart.buyerIdentity.email) {
          const { getLoyaltyEmail, isLoyaltyUser } = useLoyalty();

          if (isLoyaltyUser.value) {
            const email = getLoyaltyEmail();
            if (email) {
              const cart = await $shopifyClient.updateBuyerIdentity(this.cart.id, {
                email,
              });

              this.setCart(cart);
            }
          }
        }

        return;
      }

      if (!localStorageCheck()) {
        console.warn('no localstorage available, cannot init cart');
        return;
      }

      const cartId = localStorage.getItem(`shopify_cart_${$i18n.locale.value}_id`);

      let cart = null;

      // if no cartId is stored, create new cart
      if (!cartId) {
        // create new cart
        cart = await $shopifyClient.createCart($shopifyClient.resolveLocale());

        this.setCart(cart);
        this.persistCart(cart.id);
      } else {
        // try to get the cart from shopify
        cart = await $shopifyClient.getCart(cartId);

        if (cart && cart.id) {
          this.setCart(cart);
        } else {
          // if the cart id does not exist, remove it from storage and reset cart
          localStorage.removeItem(`shopify_cart_${$i18n.locale.value}_id`);

          this.resetCart();
        }
      }

      // finally check for voucher and giftcard in url
      const route = useRoute();

      if (route.query.voucher) {
        this.applyDiscount(route.query.voucher.split(','));
      }
    },

    async refreshCheckoutAndAttributes() {
      this.isSubmitting += 1;

      const { $shopifyClient } = useNuxtApp();

      const payload = this.lineItems
        .filter((lineItem) => {
          return lineItem.attributes.findIndex((attr) => attr.key === '_offer') !== -1;
        })
        .map((lineItem) => {
          return {
            id: lineItem.id,
            quantity: lineItem.quantity,
            attributes: [],
          };
        });

      if (payload) {
        const checkout = await $shopifyClient.updateLines(this.cart.id, payload);
        this.setCart(checkout);

        await this.pickystoryRefreshCart(checkout);
      }

      this.isSubmitting -= 1;

      return Promise.resolve();
    },

    async refreshCheckout() {
      if (!this.cart?.id) {
        return;
      }

      const { $shopifyClient } = useNuxtApp();

      this.isSubmitting += 1;
      let cart = null;

      try {
        cart = await $shopifyClient.getCart(this.cart.id);
        this.setCart(cart);
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        this.isSubmitting -= 1;
      }
    },

    async applyDiscount(discountCodes) {
      const { $shopifyClient, $i18n } = useNuxtApp();
      const toastStore = useToastStore();

      try {
        const cart = await $shopifyClient.addDiscount(
          this.cart.id,
          typeof discountCodes === 'string' ? [discountCodes] : discountCodes
        );

        this.setCart(cart);
        toastStore.add({
          message: $i18n.t('cart.discountApplied'),
          reason: 'success',
          type: 'success',
        });
      } catch (error) {
        console.log('error', error);

        toastStore.add({
          message: $i18n.t('cart.discountInvalid'),
          reason: error.message,
          type: 'error',
        });
      }
    },

    /**
     * adds lineItems to to cart of format [{ variantId, quantity}]
     */
    async addLineItems(lineItems) {
      await this.initCart();

      const { $shopifyClient } = useNuxtApp();

      try {
        const cart = await $shopifyClient.addLines(this.cart.id, lineItems);
        await this.pickystoryRefreshCart(cart);
        this.setCart(cart);
      } catch (e) {
        console.error(e);
      }
    },

    async removeLineItems(variantIds) {
      this.lineItems
        .filter((lineItem) => {
          return variantIds.some((variantId) => {
            return lineItem.variant.id.includes(variantId);
          });
        })
        .forEach((lineItem) => {
          this.changeQty({ id: lineItem.id, quantity: 0 });
        });

      await this._debouncedUpdateLineItems.flush();

      return Promise.resolve(true);
    },

    changeQty({ id, quantity }) {
      this.payload = { ...this.payload, [id]: quantity };
      this.updateLineItems();
    },

    updateLineItems() {
      this._debouncedUpdateLineItems();
    },

    async pickystoryRefreshCart() {
      if (import.meta.client && window.pickystory) {
        const { client } = window.pickystory;

        try {
          await client.refreshCart();

          return Promise.resolve(true);
        } catch (e) {
          Sentry.captureException(e);

          return Promise.resolve(false);
        }
      }

      return Promise.resolve(false);
    },

    /**
     * clear client checkout by removing the reference in local storage
     * */
    resetCart() {
      this.setCart(null);
      this.initCart();
    },

    /**
     * persist cartId to local storage
     */
    persistCart(cartId) {
      if (localStorageCheck()) {
        const { $i18n } = useNuxtApp();
        localStorage.setItem(`shopify_cart_${$i18n.locale.value}_id`, cartId);
      } else {
        console.error('localstorage is not available');
      }
    },

    trackLineItemChanges({ prevLineItems, newLineItems }) {
      const removedItems = [];
      const addedItems = [];

      // Find removed and quantity-decreased items
      prevLineItems.forEach((prevItem) => {
        const newItem = newLineItems.find((ni) => ni.id === prevItem.id);
        if (!newItem) {
          // Item was removed
          removedItems.push({ lineItem: prevItem, delta: prevItem.quantity });
        } else if (newItem.quantity < prevItem.quantity) {
          // Quantity decreased
          removedItems.push({
            lineItem: prevItem,
            delta: prevItem.quantity - newItem.quantity,
          });
        }
      });

      // Find added and quantity-increased items
      newLineItems.forEach((newItem) => {
        const prevItem = prevLineItems.find((pi) => pi.id === newItem.id);
        if (!prevItem) {
          // Item was added
          addedItems.push({ lineItem: newItem, delta: newItem.quantity });
        } else if (newItem.quantity > prevItem.quantity) {
          // Quantity increased
          addedItems.push({
            lineItem: newItem,
            delta: newItem.quantity - prevItem.quantity,
          });
        }
      });

      if (removedItems.length > 0) {
        try {
          this.gtmEECremoveFromCart(removedItems);
        } catch (e) {
          Sentry.captureException(e);
        }
      }

      if (addedItems.length > 0) {
        try {
          this.gtmEECaddToCart(addedItems);
        } catch (e) {
          Sentry.captureException(e);
        }
      }

      return Promise.resolve();
    },

    async gtmEECaddToCart(variantsDeltas) {
      const { $_gtm, $shopifyBuyClient } = useNuxtApp();

      const items = await gtmEECProducts(variantsDeltas, $shopifyBuyClient());
      const data = {
        event: 'EEC_add_to_cart',
        event_name: 'add_to_cart',
        currencyCode: this.cart.currencyCode,
        ecommerce: {
          items: items,
        },
      };
      $_gtm.push(data);
    },

    async gtmEECremoveFromCart(variantsDeltas) {
      const { $_gtm, $shopifyBuyClient } = useNuxtApp();

      // enrich lineItem with shopifyProduct data
      const items = await gtmEECProducts(variantsDeltas, $shopifyBuyClient());

      const enrichedItems = items.map((item, index) => {
        const variant = variantsDeltas[index]?.lineItem.variant;
        const product = variantsDeltas[index]?.lineItem.product;

        const imageUrl =
          variant?.image?.src || product?.images?.edges?.[0]?.node?.src || product?.featuredImage?.src || null;

        return {
          ...item,
          imageUrl,
        };
      });

      $_gtm.push({
        event: 'EEC_remove_from_cart',
        event_name: 'remove_from_cart',
        ecommerce: {
          items: enrichedItems,
        },
      });
    },

    async createOrUpdateCartAttributes(attributes = []) {
      const { $shopifyClient } = useNuxtApp();
      const newAttributes = [...this.cart.attributes, ...attributes];

      const cart = await $shopifyClient.updateAttributes(this.cart.id, newAttributes);

      this.setCart(cart);
    },

    async checkoutUrl() {
      const { $i18n, $shopifyClient, $getCookieConsent, $getCurrentLocaleConfig } = useNuxtApp();
      const { loggedIn, user, getMultipass } = useBlackrollAuth();

      if (loggedIn.value && user.value?.isVerified) {
        try {
          const multipassResponse = await getMultipass($i18n.locale.value, this.cart?.checkoutUrl);
          const locale = $getCurrentLocaleConfig();

          return `https://${locale.shopifyApiDomain}/account/login/multipass/${multipassResponse.token}`;
        } catch (error) {
          console.warn('Could not login to Shopify:', error.message);
        }
      }

      const consentSettings = $getCookieConsent();

      // if consent settings are available, add them to the cart attributes
      if (consentSettings && consentSettings.consentId?.length > 0) {
        const attributes = [
          {
            key: 'ConsentId',
            value: consentSettings.consentId,
          },
        ];

        Object.keys(consentSettings.consent).forEach((key) => {
          if (key in consentSettings.consent) {
            attributes.push({
              key: `Consent_${key}`,
              value: consentSettings.consent[key] ? 'true' : 'false',
            });
          }
        });

        await this.createOrUpdateCartAttributes(attributes);
      }

      if ($shopifyClient.resolveCountry() !== this.cart.buyerIdentity.countryCode) {
        const cart = await $shopifyClient.updateBuyerIdentity(this.cart.id, {
          ...this.cart.buyerIdentity,
          countryCode: $shopifyClient.resolveCountry(),
        });

        this.setCart(cart);
      }

      return this.cart?.checkoutUrl || null;
    },

    // Mutations converted to actions
    setCart(cart) {
      if (cart && cart.id) {
        const { $pickystory } = useNuxtApp();

        $pickystory.initPickystoryCart(cart.id);
      }

      this.cart = cart;
    },

    setFreeShippingThreshold(threshold) {
      this.freeShippingThreshold = threshold;
    },

    setSubmitting(isSubmitting) {
      this.isSubmitting += isSubmitting ? 1 : -1;
    },

    setAsyncSubmitting(isAsyncSubmitting) {
      this.isAsyncSubmitting += isAsyncSubmitting ? 1 : -1;
    },

    setPayload(item) {
      this.payload = { ...this.payload, [item.id]: item.quantity };
    },

    resetPayload() {
      this.payload = {};
    },

    resetErrors() {
      this.errors = [];
    },

    setErrors(errors) {
      this.errors = errors;
    },

    // Internal debounced update function
    _debouncedUpdateLineItems: debounce(async function () {
      // 'this' refers to the store instance when called via this._debouncedUpdateLineItems()
      const store = useCartStore();
      // Capture Nuxt context immediately - debounce runs in a new microtask where
      // Nuxt's async context may be lost (Nuxt 4 async context loss)
      const { $shopifyClient } = useNuxtApp();

      store.isSubmitting += 1;

      const payload = Object.entries(store.payload).map(([id, quantity]) => {
        return {
          id,
          quantity,
        };
      });

      store.payload = {};
      store.errors = [];

      const removeBundles = [];

      // Here we check if we need to remove any bundles.
      // If the quantity is 0, we search & remove all related bundles.
      // Bundle relationships are stored in the line items custom attribute (bundledBy)
      payload.forEach((item) => {
        if (item.quantity <= 0) {
          const currentItem = store.lineItems.find((li) => li.id === item.id);
          store.lineItems
            .filter((li) => {
              return (
                li.attributes.findIndex((attr) => {
                  return attr.key === '_bundledBy' && attr.value === currentItem?.variant?.product?.handle;
                }) !== -1
              );
            })
            .forEach((bundledItem) => {
              const bundledByProductHandle = bundledItem.attributes.find((attr) => attr.key === '_bundledBy').value;

              const countParentProducts = store.lineItems.filter(
                (item) => item.variant.product.handle === bundledByProductHandle
              ).length;

              // we remove only if there is only one parent product left
              if (countParentProducts <= 1) {
                removeBundles.push({
                  id: bundledItem.id,
                  quantity: 0,
                });
              }
            });
        }
      });

      const finalPayload = payload.concat(removeBundles);

      const prevLineItems = [...store.lineItems];

      try {
        const cart = await $shopifyClient.updateLines(store.cart.id, finalPayload);

        store.setCart(cart);
        store.pickystoryRefreshCart(cart);
      } catch (error) {
        // if an error occurs, we set the errors and cart state
        store.errors = error.userErrors;

        // we set the cart state from the error response, since it contains correct quantities
        store.setCart(error.cart);
        await store.pickystoryRefreshCart(error.cart);
      } finally {
        await store.trackLineItemChanges({
          prevLineItems,
          newLineItems: store.lineItems,
        });
        // tracking after everything is done
        store.isSubmitting -= 1;
      }
    }, 450),
  },
});
